import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Plus, Search, RefreshCw, Trash2, ArrowDownAZ } from "lucide-react";

const emptyForm = { first_name: "", last_name: "", email: "", phone: "", level: "A1", notes: "", status: "active" };

const SORTS = {
  recent: { label: "Plus récents", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  oldest: { label: "Plus anciens", fn: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") },
  alpha: { label: "Ordre alphabétique (A→Z)", fn: (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "fr") },
  alphaRev: { label: "Ordre alphabétique (Z→A)", fn: (a, b) => `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`, "fr") },
};

export default function Learners() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/coach/learners").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/coach/learners", form);
      toast.success("Apprenant créé — 40 séances générées.");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally { setSaving(false); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/l/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien apprenant copié");
  };

  const regenerate = async (id) => {
    const { data } = await api.post(`/coach/learners/${id}/regenerate-token`);
    toast.success("Nouveau lien généré");
    copyLink(data.access_token);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet apprenant et toutes ses données ?")) return;
    await api.delete(`/coach/learners/${id}`);
    toast.success("Apprenant supprimé");
    load();
  };

  const filtered = items.filter((l) => {
    const s = q.toLowerCase();
    return !s || `${l.first_name} ${l.last_name} ${l.email}`.toLowerCase().includes(s);
  }).sort(SORTS[sort].fn);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl text-[#1F2421]">Apprenants</h1>
          <p className="text-[#5C6561] mt-1">{items.length} apprenant{items.length > 1 ? "s" : ""} enregistré{items.length > 1 ? "s" : ""}.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6561]" />
            <Input data-testid="learner-search-input" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="learner-sort-select" className="bg-white w-full sm:w-56">
              <div className="flex items-center gap-2"><ArrowDownAZ className="w-4 h-4 text-[#5C6561]" /><SelectValue /></div>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-learner-button" className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white">
                <Plus className="w-4 h-4 mr-1" /> Nouvel apprenant
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle>Créer un apprenant</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prénom</Label>
                    <Input data-testid="learner-form-firstname" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                  </div>
                  <div><Label>Nom</Label>
                    <Input data-testid="learner-form-lastname" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                  </div>
                </div>
                <div><Label>Email</Label>
                  <Input data-testid="learner-form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Téléphone</Label>
                    <Input data-testid="learner-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div><Label>Niveau</Label>
                    <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                      <SelectTrigger data-testid="learner-form-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["A1","A2","B1","B2"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notes</Label>
                  <Textarea data-testid="learner-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button data-testid="create-learner-submit-button" type="submit" disabled={saving} className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Créer & générer 40 séances</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="dec-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#5C6561]">Aucun apprenant. Créez le premier avec le bouton ci-dessus.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F4EFEA] text-[#5C6561] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Nom</th>
                <th className="text-left p-3 hidden md:table-cell">Email</th>
                <th className="text-left p-3">Niveau</th>
                <th className="text-left p-3 hidden lg:table-cell">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-[#E6E1DA]" data-testid={`learner-row-${l.id}`}>
                  <td className="p-3">
                    <Link to={`/coach/apprenants/${l.id}`} className="font-medium hover:text-[#D95D39]">
                      {l.first_name} {l.last_name}
                    </Link>
                  </td>
                  <td className="p-3 hidden md:table-cell text-[#5C6561]">{l.email}</td>
                  <td className="p-3"><Badge variant="secondary">{l.level}</Badge></td>
                  <td className="p-3 hidden lg:table-cell">
                    <span className={l.status === "active" ? "status-completed dec-pill px-2 py-0.5 text-xs" : "status-notstarted dec-pill px-2 py-0.5 text-xs"}>
                      {l.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button data-testid={`copy-link-${l.id}`} variant="ghost" size="icon" onClick={() => copyLink(l.access_token)} title="Copier le lien"><Copy className="w-4 h-4" /></Button>
                      <Button data-testid={`regen-link-${l.id}`} variant="ghost" size="icon" onClick={() => regenerate(l.id)} title="Régénérer le lien"><RefreshCw className="w-4 h-4" /></Button>
                      <Button data-testid={`delete-learner-${l.id}`} variant="ghost" size="icon" onClick={() => remove(l.id)} title="Supprimer"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
